import { Crosshair, Eye, EyeOff, Loader2, Mic, PenLine, X, type LucideIcon } from "lucide-react"
import { ReferenceMediaPreview } from "@/components/reference-inputs/reference-media-preview"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { cn } from "@/lib/utils"

/** Действия кадра: видны при наведении и когда фокус внутри миниатюры. */
const THUMB_ACTION_CLASS = cn(
  "pointer-events-none opacity-0",
  "group-hover/thumb:pointer-events-auto group-hover/thumb:opacity-100",
  "group-focus-within/thumb:pointer-events-auto group-focus-within/thumb:opacity-100",
)

export type ReferenceInputThumbProps = {
  url: string | null
  mediaType?: "image" | "video" | "audio" | "voice"
  thumbUrl?: string | null
  onOpenPreview: () => void
  onRemove?: () => void
  onApply?: () => void
  applyIcon?: LucideIcon
  onSketchEdit?: () => void
  onToggleVisibility?: () => void
  sketchEditing?: boolean
  sketchEditingLabel?: string
  cornerAction?: "remove" | "visibility"
  visible?: boolean
  removing?: boolean
  openPreviewTitle: string
  removeAriaLabel?: string
  applyAriaLabel?: string
  sketchEditAriaLabel?: string
  visibilityOnAriaLabel?: string
  visibilityOffAriaLabel?: string
  className?: string
}

export function ReferenceInputThumb({
  url,
  mediaType = "image",
  thumbUrl,
  onOpenPreview,
  onRemove,
  onApply,
  applyIcon: ApplyIcon = Crosshair,
  onSketchEdit,
  onToggleVisibility,
  sketchEditing = false,
  sketchEditingLabel,
  cornerAction = "remove",
  visible = true,
  removing = false,
  openPreviewTitle,
  removeAriaLabel,
  applyAriaLabel,
  sketchEditAriaLabel,
  visibilityOnAriaLabel,
  visibilityOffAriaLabel,
  className,
}: ReferenceInputThumbProps) {
  const isVisibility = cornerAction === "visibility"
  const isVoice = mediaType === "voice"
  const visibilityLabel = visible ? visibilityOnAriaLabel : visibilityOffAriaLabel

  return (
    <div
      className={cn(
        "group/thumb relative h-16 w-16 overflow-hidden rounded-lg border border-ui-border",
        isVisibility && !visible && "opacity-40",
        className,
      )}
    >
      {isVoice ? (
        // Голосовой референс: стек «аватар героя + бейдж голоса».
        <button
          type="button"
          onClick={onOpenPreview}
          className="relative block h-full w-full cursor-zoom-in"
          title={openPreviewTitle}
          aria-label={openPreviewTitle}
        >
          {url ? (
            <img src={url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center dark:bg-ui-bg-elevated light:bg-clay-2">
              <Mic className="h-5 w-5 text-ui-text-secondary" />
            </div>
          )}
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-black/55 to-transparent" />
          <span className="absolute bottom-0.5 left-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-ui-accent text-ui-on-accent shadow-[var(--ui-shadow-sm)]">
            <Mic className="h-3 w-3" />
          </span>
        </button>
      ) : url ? (
        <button
          type="button"
          onClick={onOpenPreview}
          className="block h-full w-full cursor-zoom-in"
          title={openPreviewTitle}
          aria-label={openPreviewTitle}
        >
          <ReferenceMediaPreview
            url={url}
            thumbUrl={thumbUrl}
            mediaType={mediaType}
            className="h-full w-full"
            showVideoBadge
          />
        </button>
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-[10px] text-ui-text-secondary dark:bg-ui-bg-elevated light:bg-clay-2"
          aria-hidden
        >
          …
        </div>
      )}
      {onApply ? (
        <button
          type="button"
          onClick={onApply}
          className={cn(
            "absolute top-0.5 left-0.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full",
            "bg-[var(--ui-chip-bg)] text-ui-text-secondary transition hover:bg-ui-accent hover:text-ui-on-accent",
            THUMB_ACTION_CLASS,
          )}
          title={applyAriaLabel}
          aria-label={applyAriaLabel}
        >
          <ApplyIcon className="h-3 w-3" />
        </button>
      ) : null}
      {onSketchEdit && !sketchEditing ? (
        <button
          type="button"
          className={cn(
            "absolute right-0.5 bottom-0.5 z-10 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full",
            "bg-[var(--ui-chip-bg)] text-ui-text-secondary transition hover:bg-ui-accent hover:text-ui-on-accent",
            THUMB_ACTION_CLASS,
          )}
          title={sketchEditAriaLabel}
          aria-label={sketchEditAriaLabel}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSketchEdit()
          }}
        >
          <PenLine className="h-3 w-3" />
        </button>
      ) : null}
      {onSketchEdit && sketchEditing ? (
        <span className="pointer-events-none absolute right-0.5 bottom-0.5 z-10 inline-flex h-5 items-center gap-1 rounded-full bg-[var(--ui-chip-bg-strong)] px-1.5 text-[9px] font-medium text-ui-text-primary shadow-sm ring-1 ring-ui-border backdrop-blur-sm">
          <span>{sketchEditingLabel}</span>
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
        </span>
      ) : null}
      {isVisibility && onToggleVisibility ? (
        <ActionHintTooltip
          label={visibilityLabel}
          className={cn("absolute top-0.5 right-0.5 h-5 w-5", THUMB_ACTION_CLASS)}
        >
          <button
            type="button"
            onPointerDown={(e) => {
              e.preventDefault()
            }}
            onClick={onToggleVisibility}
            className={cn(
              "flex cursor-pointer items-center justify-center rounded-full bg-[var(--ui-chip-bg)]",
              "text-ui-text-secondary transition-colors hover:bg-ui-surface-raised hover:text-ui-text-primary",
              visibilityLabel ? "h-full w-full" : cn("absolute top-0.5 right-0.5 h-5 w-5", THUMB_ACTION_CLASS),
            )}
            aria-label={visibilityLabel}
            aria-pressed={visible}
          >
            {visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </button>
        </ActionHintTooltip>
      ) : onRemove ? (
        <button
          type="button"
          disabled={removing}
          onClick={onRemove}
          className={cn(
            "absolute top-0.5 right-0.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full",
            "bg-[var(--ui-chip-bg)] text-ui-text-secondary transition hover:bg-red-600/80 hover:text-ui-on-accent",
            removing ? "pointer-events-auto opacity-100" : THUMB_ACTION_CLASS,
          )}
          aria-label={removeAriaLabel}
        >
          {removing ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
        </button>
      ) : null}
    </div>
  )
}
